import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './Card.css';
import { useImageSource } from '../../../contexts/ImageSourceContext';

function CardInfoOverlay({
    show,
    card,
    entry,
    prices = {},
    setDisplayName,
    showExtendedInfo = false,
    imageError = false,
    onClose,
    onImageError,
    quantityBadge = null,
    // Navigation props
    onPrevious = null,
    onNext = null,
    hasPrevious = false,
    hasNext = false
}) {
    const { getImageUrl } = useImageSource();
    const [showBackDetail, setShowBackDetail] = useState(false);

    if (!show || !card) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Use context's getImageUrl for consistent image source handling
    const imagePathDetail = getImageUrl(card, showBackDetail);

    // Portal to <body>: ancestors with CSS transforms (fanned trade-card
    // stacks, mobile scaling) hijack position:fixed, which trapped this
    // overlay inside the clicked card's width on small screens
    return createPortal(
        <div className="info-overlay-fullpage" onClick={handleOverlayClick}>
            {/* Navigation Arrows */}
            {onPrevious && (
                <button
                    className="info-nav-arrow info-nav-prev"
                    onClick={(e) => { e.stopPropagation(); onPrevious(); }}
                    disabled={!hasPrevious}
                    aria-label="Previous card"
                >
                    ‹
                </button>
            )}
            {onNext && (
                <button
                    className="info-nav-arrow info-nav-next"
                    onClick={(e) => { e.stopPropagation(); onNext(); }}
                    disabled={!hasNext}
                    aria-label="Next card"
                >
                    ›
                </button>
            )}

            <div className="info-content-fullpage">
                <button className="info-close-fullpage" onClick={onClose}>
                    ×
                </button>
                <div className="info-layout">
                    {/* Left side - Large card image */}
                    <div className="info-image-section" style={{ position: 'relative' }}>
                        {(card.backside_image_path || card.local_backside_path) && (
                            <button
                                className="backside-toggle"
                                style={{ position: 'absolute', top: 10, left: 10, zIndex: 1 }}
                                onClick={e => { e.stopPropagation(); setShowBackDetail(b => !b); }}
                                aria-label={showBackDetail ? 'Show front side' : 'Show back side'}
                            >
                                ⇄
                            </button>
                        )}
                        {imagePathDetail && !imageError ? (
                            <div style={{ position: 'relative' }}>
                                <img
                                    src={imagePathDetail}
                                    alt={card.name}
                                    className="info-card-image-large"
                                    onError={onImageError}
                                />
                                {quantityBadge && (
                                    <div className="binder-quantity-badge" style={{ position: 'absolute', bottom: 12, left: 12, zIndex: 2 }}>
                                        {quantityBadge}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="info-image-placeholder-large">
                                {imageError ? 'Image Not Found' : 'No Image Available'}
                                <br />
                                <small style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '1rem' }}>
                                    {card.setCode} - {card.name}
                                </small>
                            </div>
                        )}
                    </div>

                    {/* Right side - Card details */}
                    <div className="info-details-section">
                        <div className="info-details-fullpage">
                            <div className="info-item-title-fullpage">
                                <h2 className="card-name-popup-large">{card.name}</h2>
                            </div>

                            {card.setCode && (
                                <div className="info-item-fullpage">
                                    <span className="info-label-fullpage">Set</span>
                                    <span className="info-value-fullpage">{setDisplayName || card.setCode}</span>
                                </div>
                            )}

                            {card.rarity && (
                                <div className="info-item-fullpage">
                                    <span className="info-label-fullpage">Rarity</span>
                                    <span className={`info-value-fullpage rarity-${card.rarity.toLowerCase()}`}>
                                        {card.rarity}
                                    </span>
                                </div>
                            )}

                            {/* Extended info (type, mana cost, power/toughness, text) */}
                            {showExtendedInfo && (
                                <>
                                    {card.type && (
                                        <div className="info-item-fullpage">
                                            <span className="info-label-fullpage">Type</span>
                                            <span className="info-value-fullpage">{card.type}</span>
                                        </div>
                                    )}
                                    {card.mana_cost && (
                                        <div className="info-item-fullpage">
                                            <span className="info-label-fullpage">Mana Cost</span>
                                            <span className="info-value-fullpage">{card.mana_cost}</span>
                                        </div>
                                    )}
                                    {card.power && card.toughness && (
                                        <div className="info-item-fullpage">
                                            <span className="info-label-fullpage">Power/Toughness</span>
                                            <span className="info-value-fullpage">{card.power}/{card.toughness}</span>
                                        </div>
                                    )}
                                    {card.text && (
                                        <div className="info-item-fullpage">
                                            <span className="info-label-fullpage">Text</span>
                                            <span className="info-value-fullpage">{card.text}</span>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Finish/Printing info */}
                            {entry ? (
                                entry.finish === 'any' ? (
                                    <div className="info-item-fullpage">
                                        <span className="info-label-fullpage">Printing</span>
                                        <span className="info-value-fullpage finishes">Any</span>
                                    </div>
                                ) : (
                                    <>
                                        <div className="info-item-fullpage">
                                            <span className="info-label-fullpage">Finish</span>
                                            <span className="info-value-fullpage finishes">{entry.finish}</span>
                                        </div>
                                        {/* Show prices for this specific finish */}
                                        {Object.keys(prices).length > 0 && (() => {
                                            const priceObj = prices[entry.finish];
                                            if (!priceObj) return null;

                                            const hasPriceData = priceObj.tcgplayer_price || priceObj.cardkingdom_price;
                                            if (!hasPriceData) return null;

                                            return (
                                                <>
                                                    {priceObj.tcgplayer_price && (
                                                        <div className="info-item-fullpage">
                                                            <span className="info-label-fullpage price-vendor-label">
                                                                TCGPlayer{priceObj.best_source === 'tcgplayer' ? ' ✓' : ''}
                                                            </span>
                                                            <span className={`info-value-fullpage ${priceObj.best_source === 'tcgplayer' ? 'best-price' : ''}`}>
                                                                ${priceObj.tcgplayer_price}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {priceObj.cardkingdom_price && (
                                                        <div className="info-item-fullpage">
                                                            <span className="info-label-fullpage price-vendor-label">
                                                                Card Kingdom{priceObj.best_source === 'cardkingdom' ? ' ✓' : ''}
                                                            </span>
                                                            <span className={`info-value-fullpage ${priceObj.best_source === 'cardkingdom' ? 'best-price' : ''}`}>
                                                                ${priceObj.cardkingdom_price}
                                                            </span>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </>
                                )
                            ) : (
                                /* No entry - show all finishes or first available */
                                card.finishes && Array.isArray(card.finishes) && card.finishes.length > 0 && (
                                    <div className="info-item-fullpage">
                                        <span className="info-label-fullpage">Finishes</span>
                                        <span className="info-value-fullpage finishes">
                                            {card.finishes.join(", ")}
                                        </span>
                                    </div>
                                )
                            )}

                            {/* Show all prices if no specific entry */}
                            {!entry && Object.keys(prices).length > 0 && (
                                <>
                                    {Object.entries(prices)
                                        .filter(([finish]) => card.finishes && card.finishes.includes(finish))
                                        .map(([finish, priceObj]) => {
                                            const hasPriceData = priceObj.tcgplayer_price || priceObj.cardkingdom_price;
                                            if (!hasPriceData) return null;

                                            return (
                                                <div key={finish}>
                                                    <div className="info-item-fullpage">
                                                        <span className="info-label-fullpage">{finish}</span>
                                                        <span className="info-value-fullpage"></span>
                                                    </div>
                                                    {priceObj.tcgplayer_price && (
                                                        <div className="info-item-fullpage">
                                                            <span className="info-label-fullpage price-vendor-label">
                                                                TCGPlayer{priceObj.best_source === 'tcgplayer' ? ' ✓' : ''}
                                                            </span>
                                                            <span className={`info-value-fullpage ${priceObj.best_source === 'tcgplayer' ? 'best-price' : ''}`}>
                                                                ${priceObj.tcgplayer_price}
                                                            </span>
                                                        </div>
                                                    )}
                                                    {priceObj.cardkingdom_price && (
                                                        <div className="info-item-fullpage">
                                                            <span className="info-label-fullpage price-vendor-label">
                                                                Card Kingdom{priceObj.best_source === 'cardkingdom' ? ' ✓' : ''}
                                                            </span>
                                                            <span className={`info-value-fullpage ${priceObj.best_source === 'cardkingdom' ? 'best-price' : ''}`}>
                                                                ${priceObj.cardkingdom_price}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default CardInfoOverlay;
